import _ from 'underscore';
import { Dictionary } from '@type_definitions/vendor';
import { FIELD_TYPE_INPUT, FIELD_TYPE_SEARCH, FIELD_TYPE_SELECT, IGABrowselistFormTypeValue, ValueOrigin } from '@app/main/forms/forms.service';
import { Injectable, Type } from '@angular/core';
import { DialogSize, IDialogOptions } from '../message/ga-message.service';
import { CompanyGroupAddeditComponent } from '../company/company-group-addedit.component';
import { CompanyDialogComponent } from '@app/main/company/company-dialog.component';
import { IObject } from '@type_definitions/datatypes';
import { WebshopCatalogSelectionDialogComponent } from '@app/main/webshop/catalog-selection/webshop-catalog-selection-dialog.component';
import { SubcompanyEditComponent } from '../company/subcompany-edit.component';
import { EditableTextDialogComponent } from '../editable-text/editable-text-dialog.component';
import { WebshopRoleAddComponent } from '@app/main/webshop/roles/webshop-role-add.component';
import { NewRolePreconditionService } from './preconditions/new-role-precondition.service';
import { BrowselistsPrecondition } from './browselists-precondition';
import { CustomerPermissionsDialogComponent } from '@app/main/userprofile/permissions/customer-permissions-dialog.component';
import { TicketGuideComponent } from '@app/main/ticket-guide/ticket-guide.component';
import { UserProfileWrapperComponent } from '@app/main/userprofile/user-profile-wrapper.component';
import { TICKET_WORKFLOW_TYPE_TRAINING } from '../ticket/ticket-data.service';
import { InvoiceInfoComponent } from '../invoice-info/invoice-info.component';
import { PaymentMatcherDialogComponent } from '@app/main/payment/payment-matcher/payment-matcher-dialog.component';
import { AddAccreditationsDialogComponent } from '@app/main/bp-profiles/advanced/accreditations/add-accreditations-dialog.component';
import { EditAccreditationDialogComponent } from '@app/main/bp-profiles/advanced/accreditations/edit-accreditation-dialog.component';
import { WebshopWrapperComponent } from '@app/main/webshop/webshop-wrapper.component';
import { FaqManageDialogComponent } from '@app/main/faq/manage/faq-manage-dialog.component';

export interface IGA2BrowselistInputBindings {
  model?: IObject;
  adding: boolean;
  saving: boolean;
  title: string;
  entity: string;
  queryFilters: IObject;
  browselistContext: any;
  // this can be referenced of 'dataBindings', or custom input bindings define in config
  [key: string]: any;
}

export interface IGA2BrowselistOutputBindings {
  onDialogSaving: (saving: boolean) => void;
  onSave: (model: any) => void;
  onDialogSaved: (responseData: any) => void;
  onCancel: () => void;
  [key: string]: (evt: any) => void;
}

export interface IBrowselistActionPermission {
  add?: boolean;
  edit?: boolean;
  delete?: boolean;
}

type InputBindingFn = (inputBindings: IGA2BrowselistInputBindings) => any;
type OutputBindingFn = (outputBindings: IGA2BrowselistOutputBindings) => (evt: any) => void;

export interface IBrowseListComponentAttributes {
  name: Type<any>; // component tag name with snake case formatted (e.g. invoice-info-edit)
  inputBindings?: Dictionary<InputBindingFn | any>;
  outputBindings?: Dictionary<OutputBindingFn>;
  closeButton?:boolean; // add close button manually if template is not extended from browselists-default-addedit component
}

export interface IBrowseListDataBindings {
  // idKey is the name of the primary key and needs to be defined
  // idKey will be used to look up 'id', which can be used in URL substitution
  idKey:string;
  [ key:string ]:string | number;
}

export enum GaVersion {
  GA1 = 'ga1',
  GA2 = 'ga2',
  GA3 = 'ga3'
}

export enum ReloadRows {
  SINGLE = 'SINGLE',
  ALL = 'ALL',
  NONE = 'NONE'
}

export interface IBrowseListComponentConfig {
  use:GaVersion.GA1 | GaVersion.GA2 | GaVersion.GA3;
  url?:string;
  component?:string | IBrowseListComponentAttributes;
  dialogOptions?: IDialogOptions; // dialogOptions.title possible to set title based on report cell value (entityRow)(e.g. ${Deaddco} value = 'Add comment')
  formId?:string; // required when use 'ga3' form
  context?:Dictionary<IGABrowselistFormTypeValue>;
  condition?:string;
  // [compositeKeyForm] for ga3: component will use context defined in component config rather than transforming dataBindings as 'key' in ga3 context request
  // this is for ga3 form that has multiple keys (composite keys)
  // dataBindings property config still need to be defined with multiple keys as this is used for re-fetching list after data has been saved.
  compositeKeyForm?: boolean;
}

export interface IBrowseListConfig {
  /**
   * @description which report to use
   */
  reportId?:number;

  /**
   * @description Used in buttons and dialog title. Use singular.
   * When not specified, title url parameter or GA3 context will be used.
   * Not supported for embedded reports
   */
  label?:string;

  /**
   * @description Points to a specific report row ID (used in POPUP buttons)
   */
  id?:number | string;

  /**
   * @description use stored report view
   */
  storedReport?: string;

  /**
   * @description Don't show a delete button
   */
  noDelete?:boolean;

  /**
   * @description Don't show an edit button
   */
  noEdit?:boolean;

  /**
   * @description Don't show an add button
   */
  noAdd?:boolean;

  /**
   * @description browselist will not fetch using rest path instead return entityId to browselist dialog and bind it to $scope.bindings.model
   * this is only applied when editComponent using ga2, and in this case usually defined component handle its own fetch data
   */
  noFetch?:boolean;

  /**
   * @description addRestPath will be used to save/add, possible to pass substitute variables like ${variable} getting from $scope.bindings.model if component extended from browselists-default-addedit
   * addRestPath only used when noAdd = false and component is using GA2
   * @example to achieve rest/compman/companygroups/${id} - {e.g. model.id = '44'}, just make sure id is returned from $scope.bindings.model
   */
  addRestPath?:string;

  /**
   * @description restPath will be used to fetch/update/delete, possible to pass substitute variables like ${variable} getting from report entityRow
   * when fetching data it will bind to $scope.bindings.model
   * @example rest/compman/companygroups/${Decgrid}
   */
  restPath?:string;

  /**
   * @description component will be used for add dialog in browselist
   */
  addComponent?:string | IBrowseListComponentConfig;

  /**
   * @description component will be used for edit dialog in browselist
   */
  editComponent?:IBrowseListComponentConfig | IBrowseListComponentConfig[];

  /**
   * @description data bindings will be used to get value reference from selected report row, properties here can be used in deleteRestPath,
   * ga1EditUrl, ga1AddUrl, component bindings (will be automatically snake case formatted e.g. mailId -> mail-id="<column_name>")
   * @example { mailId: 'Derpmnr' } Derpmnr will be transform to value and we can use $scope.bindings.mailId
   */
  dataBindings:IBrowseListDataBindings;

  /**
   * @description When adding a new record, it is sometimes necessary to reload the entire data table, as other rows might also be updated. The reloadRows method allows this, but a full reload is only triggered if user-modified filters are detected.
   * For ReloadRows.ALL, the edit functionality will reload all rows but only update and mark the currently edited row. In this specific case, the server does not need to respond with the ID of the edited row, as the UI already knows it (however, it can be used when it is not a composite key).
   * For ReloadRows.ALL, the add functionality will reload all rows and update the full data table.
   * default ReloadRows.SINGLE
   **/
  reloadRows?:ReloadRows;

  /**
   * @description execute a precondition function before showing the dialog
   **/
  precondition?:Type<BrowselistsPrecondition>;

  /**
   * @description set this value to true if you don't want the config to automatically load when the report is started
   */
  dontAutoLoad?:boolean;

  /**
   * @description customise the edit button from browselist
   */
  editButton?:{
    label:string;
    icon?:string;
  };
}

export enum BrowselistCode {
  ACCREDITATIONS = 'ACCREDITATIONS',
  FAQ = 'FAQ',
  CATALOG_PRODUCTS = 'CATALOG_PRODUCTS'
}

const webshopEditComponent:IBrowseListComponentConfig = {
  use: GaVersion.GA2,
  component: {
    name: WebshopWrapperComponent,
    inputBindings: {
      webshopId: (inputBindings: IGA2BrowselistInputBindings) => inputBindings.model || 'NEW',
      showCancelButton: true
    },
    outputBindings: {
      onSaved: (outputBindings: IGA2BrowselistOutputBindings) => outputBindings.onDialogSaved,
      onCancel: (outputBindings: IGA2BrowselistOutputBindings) => outputBindings.onCancel
    }
  },
  dialogOptions: {
    size: DialogSize.XL
  }
};

@Injectable()
export class BrowseListConfigService {
  private browseListConfig:Dictionary<IBrowseListConfig> = {
    AUTOMATIC_REPORT: {
      reportId: 101,
      label: 'Automatic Report',
      dataBindings: {
        idKey: 'Vwrepid',
        mailId: 'Derpmnr'
      },
      restPath: 'rest/reports/${Vwrepid}/automatic/${Derpmnr}',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/ReportMailView_Edit.jsp?row0_Derepid=${Vwrepid}&row0_Derpmnr=${Derpmnr}&allowsave=${allowsave}&ejbEvent=edit&browseList=true'
      },
      addComponent: {
        use: GaVersion.GA1,
        url: '/unacc/ReportMailView_Edit.jsp?ejbEvent=create&Detestr=1&Demaide=0&wv_Demenid=1253&wv_Deappid=366&wv_Detitle=Automatic+report+mails&browseList=true'
      }
    },
    CART_LINES: {
      reportId: 303,
      label: 'Cart Line',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/CartEditView_Edit.jsp?row0_Deordnr=${Order_number}&row0_Deolnnr=${Line_number}&ejbEvent=edit&browseList=true'
      },
      noAdd: true,
      noDelete: true,
      dataBindings: {
        idKey: 'Order_number',
        lineNumber: 'Line_number'
      }
    },
    CC: {
      reportId: 267,
      label: 'Country',
      editComponent: [
        {
          use: GaVersion.GA1,
          url: '/unacc/ComCntryView_Edit.jsp?row0_Deccoid=${Deccoid}&ejbEvent=edit&browseList=true',
          condition: '\'{Decomty_ds}\' === \'Vendor\''
        },
        {
          use: GaVersion.GA3,
          formId: 'FRM-COMPANYCOUNTRIES',
          context: {
            COMPANY: {
              value: 'Decomnm',
              type: FIELD_TYPE_SELECT,
              valueOrigin: ValueOrigin.REPORT_FILTERS
            }
          },
          dialogOptions: {
            size: DialogSize.LG
          }
        }
      ],
      addComponent: '=editComponent[1]',
      dataBindings: {
        idKey: 'Deccoid'
      },
      noDelete: true
    },
    CG: {
      reportId: 213,
      label: 'Company Group',
      addRestPath: 'rest/compman/companygroups/',
      restPath: 'rest/compman/companygroups/${Decgrid}',
      addComponent: '=editComponent',
      editComponent: {
        use: GaVersion.GA2,
        component: {
          name: CompanyGroupAddeditComponent
        }
      },
      dataBindings: {
        idKey: 'Decgrid'
      }
    },
    COMPA: {
      reportId: 266,
      label: 'Company',
      addComponent: '=editComponent',
      noFetch: true,
      editComponent: {
        use: GaVersion.GA2,
        component: {
          name:  CompanyDialogComponent,
          outputBindings: {
            onSaved: (outputBindings: IGA2BrowselistOutputBindings) => outputBindings.onDialogSaved
          }
        },
        dialogOptions: {
          size: DialogSize.LG
        }
      },
      dataBindings: {
        idKey: 'Decomid',
        companyId: 'Decomid'
      },
      noDelete: true
    },
    COUNTRY_INFO: {
      reportId: 249,
      label: 'Country Info',
      addComponent: '=editComponent',
      editComponent: {
        use: GaVersion.GA3,
        formId: 'FRM-COUNTRIES',
        dialogOptions: {
          size: DialogSize.LG
        }
      },
      dataBindings: {
        idKey: 'Decouid'
      },
      noDelete: true
    },
    DEPARTMENT_INFO: {
      reportId: 250,
      label: 'Department Info',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/C_DeptView_Edit.jsp?row0_Decdeid=${Decdeid}&ejbEvent=edit&browseList=true'
      },
      addComponent: {
        use: GaVersion.GA1,
        url: '/unacc/C_DeptView_Edit.jsp?ejbEvent=create&browseList=true'
      },
      dataBindings: {
        idKey: 'Decdeid'
      },
      noDelete: true
    },
    EXTERNALMAP_VALUE: {
      reportId: 311,
      label: 'External ID',
      addComponent: '=editComponent',
      editComponent: {
        use: GaVersion.GA3,
        formId: 'FRM-EXTERNALMAPVALUE',
        dialogOptions: {
          size: DialogSize.LG
        }
      },
      dataBindings: {
        idKey: 'Id'
      }
    },
    LOC: {
      reportId: 241,
      label: 'Location',
      restPath: 'rest/compman/locations/${Delocid}',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/C_LocatView_Edit.jsp?row0_Delocid=${Delocid}&ejbEvent=edit&browseList=true'
      },
      addComponent: {
        use: GaVersion.GA1,
        url: '/unacc/C_LocatView_Edit.jsp?Decomid=${Decomnm}&ejbEvent=create&browseList=true'
      },
      dataBindings: {
        idKey: 'Delocid'
      }
    },
    CATALOG_SELECTION: {
      reportId: 301,
      label: 'Catalog selection',
      editComponent: {
        use: GaVersion.GA2,
        component: {
          name: WebshopCatalogSelectionDialogComponent,
          outputBindings: {
            onSaved: (outputBindings: IGA2BrowselistOutputBindings) => outputBindings.onDialogSaved
          }
        }
      },
      noAdd: true,
      noDelete: true,
      noFetch: true,
      dataBindings: {
        idKey: 'Webshop_id',
        webshopId: 'Webshop_id',
        countryId: 'Country_id',
        companyCountryId: 'Bp_id',
        oemId: 'Vendor_id',
        includeInWebshopCatalog: 'Include_in_shop_catalog',
        productCategories: 'Categories_selection'
      }
    },
    LOCWEBSHOP: {
      reportId: 40,
      label: 'Address',
      restPath: 'rest/compman/locations/${Delocid}',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/C_LocatView_Edit.jsp?row0_Delocid=${Delocid}&ejbEvent=edit&browseList=true'
      },
      addComponent: {
        use: GaVersion.GA1,
        url: '/unacc/C_LocatView_Edit.jsp?Decomid=${Decomnm}&Desprid=${Desprnm}&ejbEvent=create&browseList=true'
      },
      dataBindings: {
        idKey: 'Delocid'
      }
    },
    MAIL_BLOCKER: {
      reportId: 274,
      label: 'Mail Blocker',
      addComponent: '=editComponent',
      editComponent: {
        use: GaVersion.GA3,
        formId: 'FRM-MAILBLOCKER'
      },
      dataBindings: {
        idKey: 'Denomid'
      }
    },
    MARGIN: {
      reportId: 180,
      label: 'Margin',
      addComponent: '=editComponent',
      editComponent: {
        use: GaVersion.GA3,
        formId: 'FRM-MARGINS',
        dialogOptions: {
          size: DialogSize.LG
        },
        context: {
          WEBSHOP: {
            value: 'Desprnm',
            type: FIELD_TYPE_SELECT,
            notEditableWhenFilled: true,
            valueOrigin: ValueOrigin.REPORT_FILTERS
          }
        }
      },
      dataBindings: {
        idKey: 'Demrgid'
      }
    },
    MODELS: {
      reportId: 189,
      label: 'Model',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/ModelView_Edit.jsp?row0_Demodid=${Demodid}&ejbEvent=edit&browseList=true'
      },
      addComponent: {
        use: GaVersion.GA1,
        url: '/unacc/ModelView_Edit.jsp?ejbEvent=create&browseList=true'
      },
      dataBindings: {
        idKey: 'Demodid'
      },
      noDelete: true
    },
    ORDER_EDIT: {
      reportId: 1,
      label: 'Order',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/O_OrderView_Edit.jsp?row0_Dereqid=${Dereqid}&ejbEvent=edit&browseList=true'
      },
      dataBindings: {
        idKey: 'Dereqid'
      },
      noDelete: true,
      noAdd: true
    },
    PAYMENT_MATCH: {
      reportId: 322,
      storedReport: 'UNMATCHED_TX',
      label: 'Payment Matching',
      restPath: 'rest/transaction/${Transaction_id}',
      dataBindings: {
        idKey: 'Transaction_id'
      },
      editButton: {
        label: 'Match'
      },
      editComponent: {
        use: GaVersion.GA2,
        component: {
          name: PaymentMatcherDialogComponent,
          outputBindings: {
            onSaving: (outputBindings: IGA2BrowselistOutputBindings) => outputBindings.onDialogSaving,
            onSaved: (outputBindings: IGA2BrowselistOutputBindings) => outputBindings.onDialogSaved
          }
        },
        dialogOptions: {
          size: DialogSize.FULL
        }
      },
      noAdd: true,
      noDelete: true,
      dontAutoLoad: true,
      reloadRows:ReloadRows.SINGLE
    },
    PENDINGB2BTOBP: {
      reportId: 242,
      label: 'Invoice Address',
      restPath: 'rest/orderentry/orders/${Dereqid}',
      editComponent: {
        use: GaVersion.GA2,
        component: {
          name: InvoiceInfoComponent,
          inputBindings: {
            orderData: (inputBindings: IGA2BrowselistInputBindings) => inputBindings.model
          },
          outputBindings: {
            onSaving: (outputBindings: IGA2BrowselistOutputBindings) => outputBindings.onDialogSaving,
            onSaved: (outputBindings: IGA2BrowselistOutputBindings) => outputBindings.onDialogSaved
          }
        }
      },
      addComponent: '=editComponent',
      dataBindings: {
        idKey: 'Dereqid'
      },
      noDelete: true,
      noAdd: true
    },
    POPUP_PENDING_STEPS: {
      reportId: 117,
      label: 'Pending Step',
      dataBindings: {
        idKey: 'Dewdfid'
      },
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/ReqStatusView_Edit.jsp?row0_Dewdfid=${Dewdfid}&ejbEvent=edit&browseList=true'
      },
      noAdd: true,
      noDelete: true,
      dontAutoLoad: true
    },
    POPUP_PERMISSIONS: {
      reportId: 38,
      label: 'Permissions',
      dataBindings: {
        idKey: 'Deconid'
      },
      editComponent: {
        use: GaVersion.GA2,
        component: {
          name: CustomerPermissionsDialogComponent,
          inputBindings: {
            userId: (inputBindings: IGA2BrowselistInputBindings) => inputBindings.model
          }
        }
      },
      noFetch: true,
      noAdd: true,
      noDelete: true,
      dontAutoLoad: true
    },
    POPUP_TICKET_COMMENTS: {
      reportId: 129,
      label: 'Comment',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/MaintComView_Edit.jsp?Demndid=${Demndid}&ejbEvent=create&browseList=true',
        dialogOptions: {
          title: '${Deaddco}'
        }
      },
      dataBindings: {
        idKey: 'Demndid'
      },
      dontAutoLoad: true
    },
    POPUP_TICKET_GLOBAL_TRAINING: {
      reportId: 175,
      label: 'Ticket Global Training',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/MaintDetView_Edit.jsp?browseList=true&{Deticket_global_edit}'
      },
      dataBindings: {
        idKey: 'Depcyid'
      },
      noDelete: true,
      noAdd: true,
      dontAutoLoad: true
    },
    POPUP_TICKET_LOCAL_TRAINING: {
      reportId: 175,
      label: 'Ticket Local Training',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/MaintDetView_Edit.jsp?browseList=true&{Deticket_local_edit}'
      },
      dataBindings: {
        idKey: 'Depcyid'
      },
      noDelete: true,
      noAdd: true,
      dontAutoLoad: true
    },
    POPUP_UPLOAD_INVOICE: {
      reportId: 205,
      label: 'Invoice',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/InvACSLView_Edit.jsp?ejbEvent=edit&wv_Detitle=Invoice&AllowCreation=1&wv_Template=InvACSL_BP.htm&Dereqid=${Dereqid}&Demrgty=BP&Deinvty=${Deinvty}&browseList=true'
      },
      dataBindings: {
        idKey: 'Dereqid',
        invoiceNr: 'Deinvnr'
      },
      noAdd: true,
      noDelete: true,
      dontAutoLoad: true
    },
    POPUP_WEBSHOP_SETUP: {
      reportId: 175,
      label: 'Webshop Setup',
      dataBindings: {
        idKey: 'Depcyid'
      },
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/WP_GeoView_Edit.jsp?row0_Depcyid=${Depcyid}&ejbEvent=edit&browseList=true&wv_Template=WPN_Geo_SetUp.htm'
      },
      noDelete: true,
      noAdd: true,
      dontAutoLoad: true
    },
    WEBSHOP_INFO: {
      reportId: 4,
      label: 'Webshop Info',
      noFetch: true,
      editComponent: webshopEditComponent,
      dataBindings: {
        idKey: 'Desprid'
      },
      noDelete: true,
      noAdd: true
    },
    QUOTE_EDIT: {
      reportId: 1,
      label: 'Quote',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/QuoteView_Edit.jsp?row0_Dereqid=${Dereqid}&ejbEvent=edit&browseList=true'
      },
      dataBindings: {
        idKey: 'Dereqid'
      },
      noDelete: true,
      noAdd: true
    },
    SC: {
      reportId: 216,
      label: 'Subcompany',
      addRestPath: 'rest/compman/subcompanies/',
      restPath: 'rest/compman/subcompanies/${Decsuid}',
      addComponent: '=editComponent',
      editComponent: {
        use: GaVersion.GA2,
        component: {
          name: SubcompanyEditComponent
        }
      },
      dataBindings: {
        idKey: 'Decsuid'
      }
    },
    TICKET: {
      reportId: 129,
      label: 'Ticket',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/MaintDetView_Edit.jsp?row0_Demndid=${id}&ejbEvent=edit&browseList=true',
        dialogOptions: {
          size: DialogSize.LG
        }
      },
      addComponent: {
        use: GaVersion.GA2,
        component: {
          name: TicketGuideComponent,
          inputBindings: {
            isInDialog: true
          },
          outputBindings: {
            onDialogClose: (outputBindings: IGA2BrowselistOutputBindings) => outputBindings.onDialogSaved
          }
        },
        dialogOptions: {
          windowClass: 'form-dialog',
          size: DialogSize.LG
        }
      },
      dataBindings: {
        idKey: 'Demndid'
      },
      noDelete: true
    },
    TICKET_IT: {
      reportId: 73,
      label: 'Ticket',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/MaintDetView_Edit.jsp?row0_Demndid=${id}&ejbEvent=edit&browseList=true'
      },
      addComponent: {
        use: GaVersion.GA1,
        url: '/unacc/MaintDetView_Edit.jsp?ejbEvent=create&browseList=true'
      },
      dataBindings: {
        idKey: 'Demndid'
      },
      noDelete: true
    },
    TICKET_LENOVO_ACCOUNTREVIEW: {
      reportId: 129,
      label: 'Ticket',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/MaintDetView_Edit.jsp?row0_Demndid=${id}&ejbEvent=edit&browseList=true'
      },
      addComponent: {
        use: GaVersion.GA1,
        url: '/unacc/MaintDetView_Edit.jsp?ejbEvent=create&browseList=true&Dewflty=TODO_CO&Demntty=PROGRAM&Decomid=960&Decdeid=1'
      },
      dataBindings: {
        idKey: 'Demndid'
      }
    },
    TRANSLATIONS: {
      reportId: 123,
      label: 'Translation',
      restPath: 'rest/translations/${Depronr}/${Application_code}/${Deisoce}/${Decolnm}',
      noFetch: true,
      noAdd: true,
      editComponent: {
        use: GaVersion.GA2,
        component: {
          name: EditableTextDialogComponent,
          inputBindings: {
            idKey: (inputBindings: IGA2BrowselistInputBindings) => inputBindings.model.key || inputBindings.model,
            noDelete: true
          },
          outputBindings: {
            onSaved: (outputBindings: IGA2BrowselistOutputBindings) => outputBindings.onDialogSaved
          },
          closeButton: true
        },
        dialogOptions: {
          size: DialogSize.XL
        }
      },
      dataBindings: {
        idKey: 'Delblid',
        project: 'Depronr',
        category: 'Application_code',
        key: 'Decolnm',
        languageId: 'Deisoce'
      }
    },
    USERS: {
      reportId: 31,
      label: 'User',
      noFetch: true,
      editComponent: {
        use: GaVersion.GA2,
        component: {
          name: UserProfileWrapperComponent,
          inputBindings: {
            userId: (inputBindings: IGA2BrowselistInputBindings) => inputBindings.model || 'NEW',
            showCancelButton: true
          },
          outputBindings: {
            onSaved: (outputBindings: IGA2BrowselistOutputBindings) => outputBindings.onDialogSaved
          },
          closeButton: true
        },
        dialogOptions: {
          size: DialogSize.XL
        }
      },
      addComponent: '=editComponent',
      dataBindings: {
        idKey: 'Deconid_search'
      },
      noDelete: true
    },
    USER_TRAINING: {
      reportId: 314,
      label: 'Training',
      noDelete: true,
      addComponent: {
        use: GaVersion.GA2,
        component: {
          name: TicketGuideComponent,
          inputBindings: {
            isInDialog: true,
            workflowTypes: [TICKET_WORKFLOW_TYPE_TRAINING],
            // browselistContext will contain requesterId
            requesterId: (inputBindings: IGA2BrowselistInputBindings) => <number>inputBindings.browselistContext
          },
          outputBindings: {
            onDialogClose: (outputBindings: IGA2BrowselistOutputBindings) => outputBindings.onDialogSaved
          }
        },
        dialogOptions: {
          windowClass: 'form-dialog',
          size: DialogSize.LG
        }
      },
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/MaintDetView_Edit.jsp?row0_Demndid=${id}&ejbEvent=edit&browseList=true'
      },
      dataBindings: {
        idKey: 'Ticket_id'
      }
    },
    WEBSHOPCOUNTRY_SETUP: {
      reportId: 243,
      label: 'Webshop Country Setup',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/WP_GeoView_Edit.jsp?row0_Depcyid=${id}&ejbEvent=edit&browseList=true&wv_Template=WPN_Geo_SetUp.htm'
      },
      dataBindings: {
        idKey: 'Depcyid'
      },
      noDelete: true,
      noAdd: true
    },
    CURRENCY_SOURCE: {
      reportId: 273,
      label: 'Currency source',
      dataBindings: {
        idKey: 'Deescce'
      },
      editComponent: {
        use: GaVersion.GA3,
        formId: 'FRM-CURRENCYSOURCE',
        dialogOptions: {
          size: DialogSize.LG
        }
      },
      addComponent: '=editComponent'
    },
    RATES: {
      reportId: 5,
      label: 'Currency rate',
      dataBindings: {
        idKey: 'Demunce',
        CURRENCY_SOURCE: 'Deescce',
        CURRENCY: 'Demunce'
      },
      editComponent: {
        use: GaVersion.GA3,
        formId: 'FRM-CURRENCYRATE',
        compositeKeyForm: true,
        context: {
          CURRENCY: {
            value: 'Demunce',
            type: FIELD_TYPE_INPUT,
            key: true,
            valueOrigin: ValueOrigin.REPORT_ROW_VALUE
          },
          CURRENCY_SOURCE: {
            value: 'Deescce',
            type: FIELD_TYPE_SELECT,
            key: true,
            valueOrigin: ValueOrigin.REPORT_ROW_VALUE
          }
        }
      },
      addComponent: '=editComponent'
    },
    WEBSHOP_ROLES: {
      reportId: 272,
      label: 'Webshop role',
      addRestPath: 'rest/webshoproles',
      restPath: 'rest/webshoproles/${Webshop_id}/roles/${Role_id}',
      addComponent: {
        use: GaVersion.GA2,
        component: {
          name: WebshopRoleAddComponent
        }
      },
      dataBindings: {
        idKey: 'Role_id',
        webshopId: 'Webshop_id'
      },
      noEdit: true,
      reloadRows: ReloadRows.ALL,
      precondition: NewRolePreconditionService
    },
    B2BCREDENTIALS: {
      reportId: 288,
      label: 'B2B credentials',
      addComponent: '=editComponent',
      editComponent: {
        use: GaVersion.GA3,
        formId: 'FRM-B2BCREDENTIALS'
      },
      dataBindings: {
        idKey: 'Decreid'
      }
    },
    COUNTRY_TAX: {
      reportId: 295,
      label: 'Country tax',
      addComponent: '=editComponent',
      editComponent: {
        use: GaVersion.GA3,
        formId: 'FRM-COUNTRYTAX',
        compositeKeyForm: true,
        context: {
          TAXID: {
            value: 'Tax_id',
            type: FIELD_TYPE_INPUT,
            key: true,
            valueOrigin: ValueOrigin.REPORT_ROW_VALUE
          },
          COUNTRY: {
            value: 'Country_id',
            type: FIELD_TYPE_SELECT,
            key: true,
            valueOrigin: ValueOrigin.REPORT_ROW_VALUE
          }
        }
      },
      dataBindings: {
        idKey: 'Tax_id',
        TAXID: 'Tax_id',
        COUNTRY: 'Country_id'
      }
    },
    CARRIER: {
      reportId: 280,
      label: 'Carrier',
      addComponent: '=editComponent',
      editComponent: {
        use: GaVersion.GA3,
        formId: 'FRM-CARRIERS'
      },
      dataBindings: {
        idKey: 'Decarid'
      }
    },
    ROLE_DEFS: {
      reportId: 299,
      label: 'Role Definitions',
      editComponent: {
        use: GaVersion.GA3,
        formId: 'FRM-ROLEDEFS'
      },
      dataBindings: {
        idKey: 'Contact_type_id'
      },
      noDelete: true,
      noAdd: true
    },
    BP_RELATIONS: {
      reportId: 311,
      noEdit: true,
      reloadRows: ReloadRows.ALL,
      storedReport: 'BP_RELATION',
      addComponent: {
        use: GaVersion.GA3,
        formId: 'FRM-BPRELATIONS',
        dialogOptions: {
          size: DialogSize.LG
        },
        context: {
          OWNERTYPE: {
            value: 'OWNER_TYPE',
            type: FIELD_TYPE_INPUT,
            notEditableWhenFilled: true,
            valueOrigin: ValueOrigin.COMPONENT_CONTEXT
          },
          GA_ID: {
            value: 'BP_ID',
            type: FIELD_TYPE_INPUT,
            notEditableWhenFilled: true,
            valueOrigin: ValueOrigin.COMPONENT_CONTEXT
          }
        }
      },
      dataBindings: {
        idKey: 'Id'
      },
      dontAutoLoad: true
    },
    EXTPREL: {
      reportId: 313,
      addComponent: {
        use: GaVersion.GA3,
        formId: 'FRM-EXTPARTNERRELATION',
        context: {
          RELATION_TYPE: {
            value: {
              [ValueOrigin.COMPONENT_CONTEXT]: 'RELATION_TYPE', // ga3 field id
              [ValueOrigin.REPORT_FILTERS]: 'Relation_type_val' // report column id
            },
            type: FIELD_TYPE_SELECT,
            // multiple value origin, if value in component context not found then get it from report filters
            valueOrigin: [ValueOrigin.COMPONENT_CONTEXT, ValueOrigin.REPORT_FILTERS]
          },
          CUSTOMER_COMPANY: {
            value: 'Customer_name',
            type: FIELD_TYPE_SEARCH,
            valueOrigin: ValueOrigin.REPORT_FILTERS
          },
          WEBSHOP: {
            value: 'Webshop_id',
            type: FIELD_TYPE_INPUT,
            valueOrigin: ValueOrigin.REPORT_FILTERS
          }
        },
        dialogOptions: {
          size: DialogSize.XL
        }
      },
      editComponent: {
        use: GaVersion.GA3,
        formId: 'FRM-EXTPARTNERRELATION',
        dialogOptions: {
          size: DialogSize.XL
        }
      },
      dataBindings: {
        idKey: 'Relation_id'
      }
    },
    CURRENCY: {
      reportId: 277,
      label: 'Currency',
      dataBindings: {
        idKey: 'Currency'
      },
      editComponent: {
        use: GaVersion.GA3,
        formId: 'FRM-CURRENCY',
        dialogOptions: {
          size: DialogSize.LG
        }
      },
      addComponent: '=editComponent'
    },
    WEBSHOP: {
      reportId: 75,
      label: 'Webshop',
      editComponent: webshopEditComponent,
      addComponent: '=editComponent',
      dataBindings: {
        idKey: 'Desprid'
      },
      noDelete: true,
      noFetch: true
    },
    [BrowselistCode.ACCREDITATIONS]: {
      reportId: 324,
      label: 'Vendor Accreditation',
      restPath: 'rest/bpprofiles/profiles/${Bp_id}/accreditation/${Accreditation_id}', // used for fetch and delete
      dataBindings: {
        idKey: 'Accreditation_id',
        accreditationId: 'Accreditation_id',
        accreditationName: 'Accreditation_name',
        bpId: 'Bp_id',
        vendorId: 'Vendor_id'
      },
      editComponent: {
        use: GaVersion.GA2,
        component: {
          name: EditAccreditationDialogComponent,
          outputBindings: {
            onSaving: (outputBindings:IGA2BrowselistOutputBindings) => outputBindings.onDialogSaving,
            onSaved: (outputBindings:IGA2BrowselistOutputBindings) => outputBindings.onDialogSaved
          }
        }
      },
      addComponent: {
        use: GaVersion.GA2,
        component: {
          name: AddAccreditationsDialogComponent,
          outputBindings: {
            onSaving: (outputBindings:IGA2BrowselistOutputBindings) => outputBindings.onDialogSaving,
            onSaved: (outputBindings:IGA2BrowselistOutputBindings) => outputBindings.onDialogSaved
          }
        }
      },
      dontAutoLoad: true,
      // this browselist uses reload ALL because add operation insert multiple records; in this case the response request for PUT/POST can have no data
      reloadRows: ReloadRows.ALL
    },
    BP_CONFIRM_SERVICE_CATALOG: {
      reportId: 329,
      storedReport: 'BP_PRICE_CONFIRMATION',
      label: 'Customer Service catalog - update/confirm prices',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/WP_CatalogView_Edit.jsp?row0_Deprdnr=${id}&ejbEvent=edit&Desprid=${webshopId}&Decouid=&{countryId}&browseList=true&wv_Template=WPN_Geo_SetUp.htm'
      },
      dataBindings: {
        idKey: 'Deprdnr',
        webshopId: 'Shop_id',
        countryId: 'Country_id'
      },
      noDelete: true,
      noAdd: true,
      reloadRows: ReloadRows.SINGLE
    },
    CATALOGS: {
      reportId: 106,
      storedReport: 'CATALOGS_SCREEN',
      label: 'Catalogs',
      restPath: 'rest/catalog/${Desprid}/${Decatid}',
      addComponent: {
        use: GaVersion.GA1,
        url: '/unacc/CatHeadView_Edit.jsp?ejbEvent=create&Desprid=${Desprnm}&browseList=true'
      },
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/CatHeadView_Edit.jsp?row0_Decatid=${id}&ejbEvent=edit&browseList=true'
      },
      dataBindings: {
        idKey: 'Decatid'
      },
      reloadRows: ReloadRows.SINGLE
    },
    [BrowselistCode.FAQ]: {
      reportId: 59,
      label: 'FAQ',
      dataBindings: {
        idKey: 'Defaqid',
        faqId: 'Defaqid'
      },
      editComponent: {
        use: GaVersion.GA2,
        component: {
          name: FaqManageDialogComponent,
          outputBindings: {
            onSaved: (outputBindings: IGA2BrowselistOutputBindings) => outputBindings.onDialogSaved
          }
        },
        dialogOptions: {
          size: DialogSize.XL
        }
      },
      addComponent: '=editComponent',
      dontAutoLoad: true,
      noDelete: true,
      noFetch: true,
      reloadRows: ReloadRows.SINGLE
    },
    [BrowselistCode.CATALOG_PRODUCTS]: {
      reportId: 329,
      storedReport: 'CATALOG_PRODUCTS',
      label: 'Catalog product',
      restPath: 'rest/catalog/${Shop_id}/${Country_id}/products/${Deprdnr}',
      editComponent: {
        use: GaVersion.GA1,
        url: '/unacc/WP_CatalogView_Edit.jsp?row0_Deprdnr=${id}&ejbEvent=edit&Desprid=${Shop_id}&Decouid=${Country_id}&browseList=true'
      },
      addComponent: {
        use: GaVersion.GA1,
        url: '/unacc/WP_CatalogView_Edit.jsp?ejbEvent=create&Desprid=${Shop_name}&browseList=true'
      },
      dataBindings: {
        idKey: 'Deprdnr'
      },
      reloadRows: ReloadRows.SINGLE
    }
  };

  constructor() {
    //empty
  }

  private applyDefaults(config:IBrowseListConfig):IBrowseListConfig {
    const defaults = {
      reloadRows: ReloadRows.SINGLE
    };

    return {...defaults, ...config};
  }

  public getConfig(listCode:string):IBrowseListConfig {
    const config: IBrowseListConfig = this.browseListConfig[ listCode ];
    return this.applyDefaults(config);
  }

  public findAutoLoadConfig(reportId:number) {
    //remove configs that shouldn't be automatically loaded
    const configs:IBrowseListConfig[] = _.filter(this.browseListConfig, c => c.reportId === reportId && !c.dontAutoLoad);
    if (configs.length === 1) {
      return this.applyDefaults(configs[0]);
    }
  }

  public getConfigs():Dictionary<IBrowseListConfig> {
    return this.browseListConfig;
  }
}